import React, { useState } from 'react';
import { Modal, Button, Tag, Switch } from 'antd';
import { CheckCircleFilled, RocketOutlined, CrownOutlined, FireFilled, StarFilled } from '@ant-design/icons';
import './OnboardingPricingModal.css';
import createPaymentLink from '../createCheckout/main';
import confetti from 'canvas-confetti';
import WhatsAppBanner from '../whatsappBanner/main';

// Reviews data from signup page
const REVIEWS_DATA = [
    {
        text: "We have moved our business from",
        highlightText: " Manychat to InstantDM. I am getting the same product at one-third of the price",
        author: "Yogendra Nathany - Coach"
    },
    {
        text: "I have to say this is",
        highlightText: " one of the best automation app i have used",
        author: "DULHAN JEWELS - Brand"
    },
    {
        text: "Absolutely wonderful.",
        highlightText: " The best that I have found. Super helpful and super reasonable pricing",
        author: "Victor Vigil"
    },
    {
        text: "This platform is",
        highlightText: " very good and makes automation easy",
        author: "Avinash - Coach"
    },
    {
        text: "It has",
        highlightText: " simplified our communication process. The interface is user-friendly",
        author: "Skill Radar - Coach"
    },
    {
        text: "I really like instantDM.",
        highlightText: " It has been very helpful and at such a great price.",
        author: "Mark Lee - Creator"
    },
    {
        text: "Very",
        highlightText: " easy to use, lot of great features",
        author: "MENTSMART - Business"
    },
];

// Ticker animation styles
const tickerStyles = `
@keyframes ticker-scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
}
.opm-ticker-container {
    overflow: hidden;
    width: 100%;
}
.opm-ticker-content {
    display: flex;
    gap: 12px;
    animation: ticker-scroll 20s linear infinite;
    width: fit-content;
}
.opm-ticker-content:hover {
    animation-play-state: paused;
}
`;

const OnboardingPricingModal = ({ open, onClose, messageApi, setPaymentLinkLoading, setPaymentLinkData }) => {

    const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
    const [showFreeTrialSuccess, setShowFreeTrialSuccess] = useState(false);

    // Detect currency based on timezone
    const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const showINR = user_tz === "Asia/Kolkata" || user_tz === "Asia/Calcutta";

    const plans = [
        {
            id: 'legend_pro',
            name: 'Legend Pro',
            icon: <CrownOutlined style={{ color: '#8b5cf6' }} />,
            monthly: { price: showINR ? '₹999' : '$12', id: '000003' },
            yearly: { price: showINR ? '₹833' : '$9.99', id: '0000031' },
            features: [
                'Unlimited Comment + Stories Automations',
                '800 replies/hour',
                'Facebook + Instagram',
            ],
            buttonText: 'Upgrade',
            buttonType: 'default',
            popular: false
        },
        {
            id: 'trendsetter',
            name: 'Trendsetter',
            icon: <FireFilled style={{ color: '#ef4444' }} />,
            monthly: { price: showINR ? '₹1,999' : '$29.99', id: '428337' }, // Adjusted based on context
            yearly: { price: showINR ? '₹1,666' : '$24.99', id: '428338' },
            features: [
                'Everything in Legend Pro',
                '2,000 replies/hour',
                'Flow Editor',
                'Shopify Integration',
                'Content Scheduler With Comment Automation (For 3 Months)',
                'Product Templates',
                'Viral Mode',
                'Advanced Dashboard'
            ],
            buttonText: 'Go Pro',
            buttonType: 'primary',
            popular: true,
            recommended: true
        },
        {
            id: 'trendsetter_pro',
            name: 'Trendsetter Pro',
            icon: <RocketOutlined style={{ color: '#10b981' }} />,
            monthly: { price: showINR ? '₹4,999' : '$59.99', id: 'trendsetter-pro-monthly' },
            yearly: { price: showINR ? '₹4,165' : '$49.99', id: 'trendsetter-pro-yearly' },
            features: [
                'Everything in Trendsetter',
                'Truly Unlimited',
                'Advanced Safety',
                'Priority Support',
                'Whatsapp Support',
                'Overload Handler'
            ],
            buttonText: 'Get Max',
            buttonType: 'default',
            popular: false
        }
    ];

    const freePlan = {
        id: 'free',
        name: 'Free Plan',
        icon: '🎁',
        price: showINR ? '₹0' : '$0',
        period: 'forever',
        features: [
            '1,500 Automations/month',
            'Link Unlimited Posts',
            'Comment to DM',
            'Basic Support'
        ],
        buttonText: 'Start for Free',
        buttonType: 'dashed'
    };

    const handleSelectPlan = (plan) => {
        if (plan.id === 'free') {
            setShowFreeTrialSuccess(true);
            confetti({
                particleCount: 200,
                spread: 100,
                origin: { y: 0.6 },
                zIndex: 10001 // Higher than modal
            });
        } else {
            // Call API to create payment link using monthly/yearly id
            const currency = showINR ? 'inr' : 'usd';
            const targetPlanId = billingCycle === 'yearly' ? plan.yearly.id : plan.monthly.id;

            // Check if setPaymentLinkLoading exists before calling
            if (setPaymentLinkLoading) setPaymentLinkLoading(true);

            // Close the modal immediately so user sees the payment loading/modal
            onClose();

            createPaymentLink(
                targetPlanId,
                currency,
                messageApi,
                setPaymentLinkLoading,
                setPaymentLinkData
            );
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            width={720}
            centered
            closable={false}
            className="onboarding-pricing-modal"
            maskClosable={false}
        >
            <div className="opm-content">
                {showFreeTrialSuccess ? (
                    <div className="opm-success-content" style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎉</div>
                        <h2 className="opm-title">Welcome to the Family!</h2>
                        <p className="opm-subtitle" style={{ maxWidth: '80%', margin: '0 auto 30px', fontSize: '16px', lineHeight: '1.6' }}>
                            Congratulations! You've unlocked <b>7 Days of Pro Access</b> for FREE without any additional cost.
                        </p>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '20px', marginBottom: '30px', display: 'inline-block', textAlign: 'left', minWidth: '300px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '16px' }}>What's Included:</h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Unlimited Automations (No Limits)</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> 500 Replies / Month</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Full Flow Editor Access</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Priority Support</li>
                            </ul>
                        </div>
                        <br />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                onClose();
                                window.location.href = '/home';
                            }}
                            style={{ minWidth: '200px', height: '50px', fontSize: '16px', borderRadius: '10px' }}
                        >
                            Go to Dashboard
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Inject ticker animation styles */}
                        <style>{tickerStyles}</style>

                        {/* Reviews Ticker - At Top */}
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #faf5ff 0%, #f0f9ff 100%)',
                            borderRadius: '12px',
                            border: '1px solid #e9d5ff'
                        }}>
                            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '3px', marginBottom: '2px' }}>
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <StarFilled key={i} style={{ color: '#facc15', fontSize: '14px' }} />
                                    ))}
                                </div>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>InstantDM - Trusted by 30,000+ Brands, Stores & Creators</span>
                            </div>
                            <div className="opm-ticker-container">
                                <div className="opm-ticker-content">
                                    {[...REVIEWS_DATA, ...REVIEWS_DATA].map((review, idx) => (
                                        <div key={idx} style={{
                                            minWidth: '200px',
                                            padding: '10px',
                                            background: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                            fontSize: '11px',
                                            flexShrink: 0
                                        }}>
                                            <p style={{ margin: '0 0 6px 0', color: '#374151', lineHeight: '1.3' }}>
                                                "{review.text}<span style={{ color: '#7c3aed', fontWeight: 600 }}>{review.highlightText}</span>"
                                            </p>
                                            <p style={{ margin: 0, fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>
                                                — {review.author}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* WhatsApp Support Banner - At Top */}
                        <WhatsAppBanner />

                        <div className="opm-header">
                            <h2 className="opm-title">Choose a Plan to Continue 🚀</h2>
                            <p className="opm-subtitle">
                                Your automation is live! Pick a plan to continue growing.
                            </p>

                            <div className="opm-billing-toggle" style={{ marginTop: '20px' }}>
                                <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                                <Switch
                                    checked={billingCycle === 'yearly'}
                                    onChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
                                    style={{ margin: '0 10px', backgroundColor: billingCycle === 'yearly' ? '#6366f1' : '#bfbfbf' }}
                                />
                                <span className={billingCycle === 'yearly' ? 'active' : ''}>
                                    Yearly <Tag color="green" style={{ marginLeft: '5px', borderRadius: '10px' }}>Save 20%</Tag>
                                </span>
                            </div>
                        </div>

                        {/* Free Plan - At Top */}
                        <div className="opm-free-plan" style={{ marginBottom: '20px' }}>
                            <div className="opm-free-header">
                                <span className="opm-free-icon">{freePlan.icon}</span>
                                <div>
                                    <h4 className="opm-free-title">{freePlan.name}</h4>
                                    <p className="opm-free-desc">500 Automations / month</p>
                                </div>
                            </div>
                            <div className="opm-free-action">
                                <Button
                                    type={freePlan.buttonType}
                                    size="large"
                                    className="opm-free-btn"
                                    onClick={() => handleSelectPlan(freePlan)}
                                >
                                    {freePlan.buttonText}
                                </Button>
                            </div>
                        </div>

                        <div className="opm-plans">
                            {plans.map((plan) => (
                                <div
                                    key={plan.id}
                                    className={`opm-plan-card ${plan.recommended ? 'recommended' : ''}`}
                                >
                                    {plan.recommended && (
                                        <div className="opm-popular-badge">Value for Money</div>
                                    )}

                                    <div className="opm-plan-icon">
                                        {typeof plan.icon === 'string' ? plan.icon : plan.icon}
                                    </div>

                                    <h3 className="opm-plan-name">{plan.name}</h3>

                                    <div className="opm-plan-price">
                                        <span className="opm-price">
                                            {billingCycle === 'yearly' ? plan.yearly.price : plan.monthly.price}
                                        </span>
                                        <span className="opm-period">/month <br />
                                            {billingCycle === 'yearly' &&
                                                <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#999' }}>(billed yearly)</span>
                                            }
                                        </span>
                                    </div>

                                    <ul className="opm-features">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx}>
                                                <CheckCircleFilled className="opm-check" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    <Button
                                        type={plan.buttonType}
                                        size="large"
                                        className={`opm-plan-btn ${plan.recommended ? 'recommended' : ''}`}
                                        onClick={() => handleSelectPlan(plan)}
                                        block
                                    >
                                        {plan.buttonText}
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <p className="opm-footer-note">
                            💡 You can upgrade or change your plan anytime from Settings
                        </p>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default OnboardingPricingModal;
